import { useEffect, useState } from "react";
import { GiFarmer } from "react-icons/gi";
import { IMaskInput } from "react-imask";
import AjudaTela from "../../components/AjudaTela";
import { LuCopyPlus } from "react-icons/lu";
import Layout from "../../components/Layout";
import ModalCadastroFazenda from "../../components/modais/ModalCadastroFazenda";
import { repositorioFazenda, repositorioFuncionario } from "../../sync/repositories/fazendas";

const AJUDA = {
  titulo: "Cadastro de Funcionários",
  descricao:
    "Cadastre aqui as pessoas que trabalham na fazenda. Esse cadastro é usado depois nas Atividades e nos Talhões para indicar quem é o responsável por cada tarefa, além de servir de base para o cálculo de custo de mão de obra.",
  secoes: [
    {
      titulo: "Dados do Funcionário",
      campos: [
        {
          nome: "Nome Completo",
          obrigatorio: true,
          explicacao:
            "Nome completo do funcionário, usado para identificá-lo nas listas de responsável em Talhões e Atividades.",
        },
        {
          nome: "CPF",
          obrigatorio: true,
          explicacao: "Documento do funcionário, digitado com máscara automática (000.000.000-00).",
        },
        {
          nome: "Fazenda",
          obrigatorio: true,
          explicacao:
            "Fazenda em que o funcionário trabalha. Só aparecem nas listas de responsável de Talhões e Atividades os funcionários vinculados à mesma fazenda selecionada.",
        },
        {
          nome: "Cargo",
          obrigatorio: false,
          explicacao: "Função exercida pelo funcionário (ex.: Tratorista, Administrador), apenas para referência.",
        },
        {
          nome: "Custo por Hora",
          obrigatorio: true,
          explicacao:
            "Valor em R$ que representa o custo da hora de trabalho desse funcionário. É usado para calcular o custo de mão de obra nas Atividades em que ele estiver envolvido.",
        },
      ],
    },
  ],
  dicas: [
    "Excluir um funcionário com registros vinculados (como Atividades ou Talhões em que ele é responsável) não é permitido — verifique se não há vínculos antes de excluir.",
    "O funcionário só aparece nas listas de responsável de outras telas se estiver cadastrado na mesma fazenda selecionada naquele formulário.",
  ],
};

export default function GestaoFuncionarios() {
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [funcionarios, setFuncionarios] = useState([]);
  const [funcionariosFiltrados, setFuncionariosFiltrados] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [mostrarModalFazenda, setMostrarModalFazenda] = useState(false);
  const [fazendas, setFazendas] = useState([]);
  
  const [filtros, setFiltros] = useState({
    busca: ""
  });
  
  const [formData, setFormData] = useState({
    nome: "",
    cpf: "",
    fazenda: "",
    cargo: "",
    custo_hora: "",
  });

  // Carregar fazendas
  const carregarFazendas = async () => {
    try {
      const response = await repositorioFazenda.listar();
      setFazendas(response.data.results || response.data);
    } catch (error) {
      console.error("Erro ao buscar fazendas:", error);
    }
  };

  useEffect(() => {
    carregarFazendas();
  }, []);

  // Carregar funcionários
  useEffect(() => {
    carregarFuncionarios();
  }, []);

  const carregarFuncionarios = async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioFuncionario.listar();

      const dados = response.data.results || response.data;
      setFuncionarios(dados);
      setFuncionariosFiltrados(dados);
    } catch (error) {
      console.error("Erro ao carregar funcionários:", error);
      alert("Erro ao carregar lista de funcionários");
    } finally {
      setCarregandoLista(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim()) {
      alert("Preencha o nome do funcionário.");
      return;
    }
    if (!formData.fazenda) {
      alert("Selecione uma fazenda.");
      return;
    }
    
    setLoading(true);

    const payload = {
      ...formData,
      custo_hora: formData.custo_hora ? Number(formData.custo_hora) : null,
    };

    try {
      if (editando) {
        // Editar funcionário existente
        const response = await repositorioFuncionario.atualizar(editandoId, payload);
        if (response.status === 200) {
          alert("Funcionário atualizado com sucesso!");
          handleCancel();
          carregarFuncionarios();
        }
      } else {
        // Cadastrar novo funcionário
        const response = await repositorioFuncionario.criar(payload);
        if (response.status === 201 || response.status === 200) {
          alert("Funcionário cadastrado com sucesso!");
          handleCancel();
          carregarFuncionarios();
        }
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (funcionario) => {
    setFormData({
      nome: funcionario.nome || "",
      cpf: funcionario.cpf || "",
      fazenda: funcionario.fazenda?.id || funcionario.fazenda || "",
      cargo: funcionario.cargo || "",
      custo_hora: funcionario.custo_hora || "",
    });
    setEditando(true);
    setEditandoId(funcionario.id);
    setMostrarFormulario(true);
    
    // Scroll para o topo para ver o formulário
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovoFuncionario = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      nome: "",
      cpf: "",
      fazenda: "",
      cargo: "",
      custo_hora: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este funcionário? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioFuncionario.excluir(id);
      alert("Funcionário excluído com sucesso!");
      carregarFuncionarios();
      
      // Se estava editando este funcionário, limpar formulário
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir funcionário. Verifique se não há registros vinculados.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = [...funcionarios];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(f => 
        f.nome?.toLowerCase().includes(buscaLower) ||
        f.cpf?.includes(buscaLower) ||
        f.cargo?.toLowerCase().includes(buscaLower) ||
        f.fazenda_nome?.toLowerCase().includes(buscaLower)
      );
    }
    
    setFuncionariosFiltrados(filtrados);
  };

  // Limpar filtros
  const limparFiltros = () => {
    setFiltros({ busca: "" });
    setFuncionariosFiltrados(funcionarios);
  };

  // Formatar CPF para exibição
  const formatarCPF = (cpf) => {
    if (!cpf) return "—";
    return cpf;
  };

  // Formatar custo por hora
  const formatarCustoHora = (custo) => {
    if (!custo && custo !== 0) return "—";
    return `R$ ${Number(custo).toFixed(2)}/h`;
  };

  // Obter nome da fazenda
  const getFazendaNome = (funcionario) => {
    if (funcionario.fazenda_nome) return funcionario.fazenda_nome;
    if (funcionario.fazenda?.nome) return funcionario.fazenda.nome;
    const fazenda = fazendas.find(f => f.id === (funcionario.fazenda?.id || funcionario.fazenda));
    return fazenda ? fazenda.nome : "—";
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Funcionários <GiFarmer size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoFuncionario}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Funcionário
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Funcionário" : "Cadastrar Novo Funcionário"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div className="flex gap-4 flex-wrap">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Nome Completo *</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      className="w-80 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Digite o nome do funcionário..."
                      required
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">CPF *</label>
                    <IMaskInput
                      mask="000.000.000-00"
                      value={formData.cpf}
                      onAccept={(value) => setFormData(prev => ({ ...prev, cpf: value }))}
                      className="w-64 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="000.000.000-00"
                      required
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Fazenda *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="fazenda"
                        value={formData.fazenda}
                        onChange={handleChange}
                        className="w-48 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione uma fazenda</option>
                        {fazendas.map(f => (
                          <option key={f.id} value={f.id}>{f.nome}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalFazenda(true)}
                        title="Cadastrar nova fazenda"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 flex-wrap">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Cargo</label>
                    <input
                      type="text"
                      name="cargo"
                      value={formData.cargo}
                      onChange={handleChange}
                      className="w-64 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Ex: Tratorista, Administrador..."
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Custo por Hora *</label>
                    <div className="flex items-center border border-green-500 rounded">
                      <input
                        type="number"
                        name="custo_hora"
                        value={formData.custo_hora}
                        onChange={handleChange}
                        className="w-28 p-2 outline-none rounded-l focus:ring-2 focus:ring-green-300"
                        required
                        step="0.01"
                        min="0"
                        placeholder="0,00"
                      />
                      <div className="h-6 w-px bg-green-500" />
                      <span className="px-3 text-gray-700 bg-gray-100 rounded-r">R$/h</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Nome, CPF, Cargo ou Fazenda</label>
                  <input
                    type="text"
                    placeholder="Digite o nome, CPF, cargo ou fazenda..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Funcionários */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {funcionariosFiltrados.length} funcionário(s) encontrado(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Nome</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">CPF</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Fazenda</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Cargo</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Custo por Hora</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {funcionariosFiltrados.map((funcionario) => (
                        <tr key={funcionario.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{funcionario.id}</td>
                          <td className="p-3 text-sm font-medium">{funcionario.nome}</td>
                          <td className="p-3 text-sm font-mono">{formatarCPF(funcionario.cpf)}</td>
                          <td className="p-3 text-sm">{getFazendaNome(funcionario)}</td>
                          <td className="p-3 text-sm">{funcionario.cargo || "—"}</td>
                          <td className="p-3 text-sm font-medium">{formatarCustoHora(funcionario.custo_hora)}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(funcionario)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(funcionario.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Excluir"
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      
                      {funcionariosFiltrados.length === 0 && (
                        <tr>
                          <td colSpan="7" className="p-8 text-center text-gray-500">
                            {filtros.busca 
                              ? "Nenhum funcionário encontrado com os filtros selecionados."
                              : "Nenhum funcionário cadastrado."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <ModalCadastroFazenda
        isOpen={mostrarModalFazenda}
        onClose={() => setMostrarModalFazenda(false)}
        onSuccess={async (nova) => {
          await carregarFazendas();
          setFormData((prev) => ({ ...prev, fazenda: nova.id }));
          setMostrarModalFazenda(false);
        }}
      />
    </Layout>
  );
}